import React, { useState, useCallback } from 'react';

import classes from './helpCenterSidebar.module.css';

interface Article {
  id: string;
  title: string;
  body: string;
}

interface Collection {
  id: string;
  name: string;
  description: string;
  children: Collection[];
  articles: Article[];
}

interface Props {
  collections: Collection[];
  visibleArticleIds: Set<string>;
  activeArticleId: string | null;
  onArticleClick: (articleId: string) => void;
  open: boolean;
}

function CollectionNode({
  collection,
  visibleArticleIds,
  activeArticleId,
  onArticleClick,
  depth,
}: {
  collection: Collection;
  visibleArticleIds: Set<string>;
  activeArticleId: string | null;
  onArticleClick: (id: string) => void;
  depth: number;
}) {
  const [expanded, setExpanded] = useState(depth === 0);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  const hasVisibleArticles = collection.articles.some((a) => visibleArticleIds.has(a.id));
  const hasVisibleChildren = collection.children.some(
    (child) =>
      child.articles.some((a) => visibleArticleIds.has(a.id)) ||
      child.children.length > 0
  );

  if (!hasVisibleArticles && !hasVisibleChildren) {
    return null;
  }

  return (
    <div className={classes.collection}>
      <button type="button" className={classes.collectionHeader} onClick={toggle}>
        <span
          className={`${classes.collectionArrow} ${expanded ? classes.collectionArrowExpanded : ''}`}
        >
          &#9654;
        </span>
        <span className={classes.collectionName}>{collection.name}</span>
      </button>

      {expanded && (
        <>
          {collection.articles.length > 0 && (
            <ul className={classes.articleList}>
              {collection.articles.map((article) => (
                <li key={article.id}>
                  <button
                    type="button"
                    className={`${classes.articleItem} ${
                      activeArticleId === article.id ? classes.articleItemActive : ''
                    } ${!visibleArticleIds.has(article.id) ? classes.articleItemHidden : ''}`}
                    onClick={() => onArticleClick(article.id)}
                  >
                    {article.title}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {collection.children.length > 0 && (
            <div className={classes.collectionChildren}>
              {collection.children.map((child) => (
                <CollectionNode
                  key={child.id}
                  collection={child}
                  visibleArticleIds={visibleArticleIds}
                  activeArticleId={activeArticleId}
                  onArticleClick={onArticleClick}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function HelpCenterSidebar({
  collections,
  visibleArticleIds,
  activeArticleId,
  onArticleClick,
  open,
}: Props) {
  return (
    <nav className={`${classes.sidebar} ${!open ? classes.sidebarHidden : ''}`}>
      {collections.map((collection) => (
        <CollectionNode
          key={collection.id}
          collection={collection}
          visibleArticleIds={visibleArticleIds}
          activeArticleId={activeArticleId}
          onArticleClick={onArticleClick}
          depth={0}
        />
      ))}
    </nav>
  );
}
